import { useEffect, useId, useRef, useState } from 'react'
import type { BillingStatusResponse, ServerApiClient } from '../../../data/ServerApiClient'
import { openCfoBillingPortal, startCfoCheckout } from './cfoBilling'
import { trimStr } from './cfoFormat'
import { invalidateCfoBootstrapCache, useCfoBootstrap } from './useCfoBootstrap'
import './CfoPlanBanner.css'

type PlanChoice = 'essentials' | 'pro'
type BillingInterval = 'monthly' | 'annual'
type BillingAccess = {
  workbookId: string
  state: 'loading' | 'ready' | 'unavailable' | 'error'
  status?: BillingStatusResponse
}

const PLAN_OPTIONS = [
  { code: 'essentials', name: 'Essentials', monthly: 99, annualMonthly: 79, description: 'Financial reporting and your core CFO tools.' },
  { code: 'pro', name: 'Pro', monthly: 349, annualMonthly: 279, description: 'Everything in Essentials, plus forecasting and modeled cash actions.' },
] as const

function isBillingPermissionError(error: unknown): boolean {
  return /\b403\b|tenant administrator access is required/i.test(trimStr((error as any)?.message || error))
}

/** One compact notice; plan selection and confirmation belong in the dialog. */
export function CfoPlanBanner({ appApi, workbookId }: { appApi: ServerApiClient; workbookId: string | null }) {
  // Interior-page tests and adapted providers can supply a partial API.
  const bootstrapCapable = typeof appApi?.getQuickBooksCfoCoreBootstrap === 'function'
    || typeof appApi?.getCfoCoreBootstrap === 'function'
  const wb = trimStr(workbookId)
  const { bootstrap } = useCfoBootstrap(appApi, bootstrapCapable ? workbookId : null)
  const [access, setAccess] = useState<BillingAccess>({ workbookId: '', state: 'loading' })
  const [accessRevision, setAccessRevision] = useState(0)
  const [busy, setBusy] = useState('')
  const busyRef = useRef(false)
  const [error, setError] = useState('')
  const [justReturned, setJustReturned] = useState<'success' | 'canceled' | null>(null)
  const [showPlans, setShowPlans] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<PlanChoice>('pro')
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly')
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    let canceled = false
    setShowPlans(false)
    setError('')
    if (!wb || !bootstrapCapable || typeof appApi.billingStatus !== 'function') {
      setAccess({ workbookId: wb, state: 'unavailable' })
      return
    }
    setAccess({ workbookId: wb, state: 'loading' })
    Promise.resolve().then(() => appApi.billingStatus(wb)).then((status) => {
      if (!canceled) setAccess({ workbookId: wb, state: 'ready', status })
    }).catch(() => {
      if (!canceled) setAccess({ workbookId: wb, state: 'error' })
    })
    return () => { canceled = true }
  }, [appApi, wb, bootstrapCapable, accessRevision])

  // A return URL is not proof of a live subscription. Read server status first.
  useEffect(() => {
    setJustReturned(null)
    try {
      const url = new URL(window.location.href)
      const marker = url.searchParams.get('billing')
      if (marker) {
        url.searchParams.delete('billing')
        window.history.replaceState(null, '', url.toString())
      }
      if (marker === 'success' || marker === 'canceled') {
        setJustReturned(marker)
        if (marker === 'success') invalidateCfoBootstrapCache(workbookId)
      }
    } catch { /* A malformed URL cannot confer billing access. */ }
  }, [workbookId])

  const currentAccess = access.workbookId === wb ? access : { workbookId: wb, state: 'loading' as const }
  const billing = currentAccess.state === 'ready' ? currentAccess.status : undefined
  const canManage = billing?.can_manage_billing === true && billing.configured === true
  const plan = bootstrap?.plan
  const planCode = trimStr(billing ? billing.plan_code : plan?.plan_code)
  const planStatus = trimStr(billing ? billing.plan_status : plan?.plan_status)
  const hasSubscription = billing ? billing.has_subscription === true : plan?.has_subscription === true
  const entitled = ['active', 'trialing', 'past_due'].includes(planStatus)
  const trialEnd = trimStr(billing ? billing.trial_ends_at : plan?.trial_ends_at)
  const trialRemainingMs = trialEnd ? new Date(trialEnd).getTime() - nowMs : NaN
  const trialDaysLeft = Number.isFinite(trialRemainingMs) ? Math.max(0, Math.ceil(trialRemainingMs / 86_400_000)) : null
  const trialRunning = trialDaysLeft == null ? Boolean(plan?.trial_active) : trialRemainingMs > 0
  const permissionReason = currentAccess.state === 'loading'
    ? 'Checking billing access…'
    : currentAccess.state === 'error'
      ? 'We couldn’t check billing access. Reload this page to try again.'
      : billing?.can_manage_billing === false
        ? 'Contact your tenant administrator to manage billing.'
        : 'Billing is currently unavailable. Contact your tenant administrator.'

  const run = async (key: 'checkout' | 'portal') => {
    if (!canManage || busyRef.current) return
    busyRef.current = true
    setBusy(key)
    setError('')
    try {
      if (key === 'checkout') await startCfoCheckout(appApi, wb, selectedPlan, billingInterval)
      else await openCfoBillingPortal(appApi, wb)
    } catch (cause) {
      if (isBillingPermissionError(cause)) {
        setAccess({ workbookId: wb, state: 'ready', status: { ...billing!, can_manage_billing: false } })
        setShowPlans(false)
        setError('Your billing access has changed. Contact your tenant administrator.')
      } else {
        setError(key === 'checkout'
          ? 'Checkout couldn’t be opened. Your selection is saved. Please try again.'
          : 'Billing couldn’t be opened. Please try again.')
      }
      setBusy('')
      busyRef.current = false
    }
  }

  if (justReturned === 'success') {
    const confirmed = billing?.has_subscription === true && ['active', 'trialing'].includes(trimStr(billing.plan_status))
    return (
      <article className="cfo-plan-banner" role="status">
        <div className="cfo-plan-banner-copy">
          <strong>{confirmed ? 'Your subscription is confirmed' : 'Confirming your subscription'}</strong>
          <p>{confirmed ? 'Your plan is ready to use.' : 'Checkout returned successfully. We’re waiting for the billing service to confirm your plan.'}</p>
          {!confirmed && currentAccess.state === 'error' ? <p>Billing status couldn’t be checked. You can check again below.</p> : null}
        </div>
        {!confirmed ? <button type="button" className="btn ghost" disabled={currentAccess.state === 'loading'} onClick={() => setAccessRevision((value) => value + 1)}>Check billing status</button> : null}
      </article>
    )
  }

  if (!planCode || !wb || (entitled && planStatus !== 'past_due')) return null

  const pastDue = planStatus === 'past_due'
  const partner = planCode === 'partner'
  const trialName = partner ? 'Partner' : 'Pro'
  const headline = pastDue ? 'Your payment method needs attention'
    : trialRunning ? (trialDaysLeft == null ? `Your ${trialName} trial is running` : `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left in your ${trialName} trial`)
      : `Your ${trialName} trial has ended`
  const body = pastDue ? 'Update your payment method to resolve the issue with your subscription.'
    : partner ? 'Partner billing follows your provisioned client count.'
      : trialRunning ? 'Explore all Pro features during your trial. Review plans when you’re ready.'
        : 'Essentials features remain available. Choose a plan to continue with Pro features.'

  return (
    <>
      <article className={`cfo-plan-banner${pastDue ? ' cfo-plan-banner-payment' : ''}`} role={pastDue ? 'alert' : 'status'}>
        <div className="cfo-plan-banner-copy">
          <strong>{headline}</strong>
          <p>{body}</p>
          {!canManage ? <p className="cfo-plan-banner-access">{permissionReason}</p> : null}
          {justReturned === 'canceled' ? <p>Checkout was canceled. Your plan has not changed.</p> : null}
          {error && !showPlans ? <p className="cfo-plan-banner-error" role="alert">{error}</p> : null}
        </div>
        {canManage || billing?.can_manage_billing !== false ? (
          <button
            type="button"
            className="btn"
            disabled={!canManage || busy !== ''}
            aria-haspopup={!pastDue && !partner ? 'dialog' : undefined}
            onClick={() => {
              if (!canManage) return
              if (pastDue) void run('portal')
              else if (partner) window.location.assign(`/billing/partner?workbook_id=${encodeURIComponent(wb)}`)
              else { setError(''); setShowPlans(true) }
            }}
          >
            {busy === 'portal' ? 'Opening billing…' : pastDue ? 'Update payment method' : partner ? 'Review Partner billing' : 'Review plans'}
          </button>
        ) : null}
      </article>
      {showPlans && canManage ? (
        <CfoPlanDialog
          selectedPlan={selectedPlan} interval={billingInterval} onPlan={setSelectedPlan} onInterval={setBillingInterval}
          busy={busy !== ''} error={error} hasSubscription={hasSubscription}
          onConfirm={() => { void run('checkout') }} onPortal={() => { void run('portal') }} onClose={() => setShowPlans(false)}
        />
      ) : null}
    </>
  )
}

function CfoPlanDialog({ selectedPlan, interval, onPlan, onInterval, busy, error, hasSubscription, onConfirm, onPortal, onClose }: {
  selectedPlan: PlanChoice; interval: BillingInterval; onPlan: (plan: PlanChoice) => void; onInterval: (interval: BillingInterval) => void
  busy: boolean; error: string; hasSubscription: boolean; onConfirm: () => void; onPortal: () => void; onClose: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const descriptionId = useId()
  const selected = PLAN_OPTIONS.find((option) => option.code === selectedPlan)!
  const amount = interval === 'annual' ? selected.annualMonthly * 12 : selected.monthly
  const total = `$${amount.toLocaleString('en-US')}`

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const node = dialogRef.current
    node?.showModal()
    node?.querySelector<HTMLButtonElement>('.cfo-plan-dialog-close')?.focus()
    return () => {
      node?.close()
      previous?.focus()
    }
  }, [])

  return (
    <dialog
      ref={dialogRef} className="cfo-plan-dialog" aria-labelledby={titleId} aria-describedby={descriptionId}
      onCancel={(event) => { event.preventDefault(); if (!busy) onClose() }}
      onClick={(event) => { if (event.target === event.currentTarget && !busy) onClose() }}
    >
      <div className="cfo-plan-dialog-panel">
        <header className="cfo-plan-dialog-header">
          <div><h2 id={titleId}>Choose your plan</h2><p id={descriptionId}>Select a plan and billing schedule, then review the details in secure checkout.</p></div>
          <button type="button" className="btn ghost cfo-plan-dialog-close" aria-label="Close plan selection" disabled={busy} onClick={onClose}>×</button>
        </header>
        <fieldset className="cfo-plan-interval" disabled={busy}>
          <legend>Billing schedule</legend>
          {(['monthly', 'annual'] as const).map((choice) => (
            <label key={choice} className={interval === choice ? 'is-selected' : ''}>
              <input type="radio" name={`${titleId}-interval`} value={choice} checked={interval === choice} onChange={() => onInterval(choice)} />
              {choice === 'monthly' ? 'Monthly' : 'Annual · save 20%'}
            </label>
          ))}
        </fieldset>
        <fieldset className="cfo-plan-options" disabled={busy}>
          <legend>Plan</legend>
          {PLAN_OPTIONS.map((option) => (
            <label key={option.code} className={`cfo-plan-option${selectedPlan === option.code ? ' is-selected' : ''}`}>
              <span className="cfo-plan-option-heading"><strong>{option.name}</strong><input type="radio" name={`${titleId}-plan`} value={option.code} checked={selectedPlan === option.code} onChange={() => onPlan(option.code)} /></span>
              <span className="cfo-plan-option-price">${interval === 'annual' ? option.annualMonthly : option.monthly}<small> / month</small></span>
              <span className="cfo-plan-option-total">{interval === 'annual' ? `$${(option.annualMonthly * 12).toLocaleString('en-US')} billed annually` : 'Billed monthly'}</span>
              <span className="cfo-plan-option-description">{option.description}</span>
            </label>
          ))}
        </fieldset>
        <div className="cfo-plan-confirmation" aria-live="polite"><strong>{selected.name} · {interval}</strong><span>{total} {interval === 'annual' ? 'per year' : 'per month'}</span></div>
        <p className="cfo-plan-checkout-note">Review your billing start date and total in checkout before subscribing.</p>
        {error ? <p className="cfo-plan-banner-error" role="alert">{error}</p> : null}
        <footer className="cfo-plan-dialog-footer">
          {hasSubscription ? <button type="button" className="btn ghost" disabled={busy} onClick={onPortal}>Manage existing subscription</button> : null}
          <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="btn" disabled={busy} onClick={onConfirm}>{busy ? 'Opening secure checkout…' : `Continue with ${selected.name}`}</button>
        </footer>
      </div>
    </dialog>
  )
}
